import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, Search, Eye, Trash2, Edit, Shield, CheckCircle, AlertTriangle, XCircle, FileText, ClipboardList, ChevronDown, ChevronUp, Compass, ArrowRight } from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import api from '../api';
import toast from 'react-hot-toast';
import UploadDocumentsModal from '../components/UploadDocumentsModal';
import EditDocumentModal from '../components/EditDocumentModal';
import StreamingAnalysisModal from '../components/StreamingAnalysisModal';
import SecurityDashboard from '../components/SecurityDashboard';
import RCSAProcessBuilder from './RCSAProcessBuilder';
import { resumeAfterUpload as resumeSmartRouterAfterUpload } from '../services/smartStartApi';

interface Document {
  id: number;
  original_name: string;
  filename?: string;
  file_type: string;
  file_size: number;
  category: string;
  description: string;
  analysis_instructions: string;
  analysis_status: 'pending' | 'pending_selection' | 'processing' | 'completed' | 'failed' | 'analyzing';
  failure_reason?: string | null;
  metadata: any;
  file_path?: string;
  converted_pdf_path?: string;
  upload_date: string;
  // NEW: PII Security fields
  pii_status: 'pending' | 'approved' | 'sanitized' | 'blocked';
  pii_risk_level: 'low' | 'medium' | 'high' | 'critical';
  pii_types_detected: string[];
  pii_scan_date: string | null;
  sanitized_content_path?: string;
  security_status: 'APPROVED' | 'SANITIZED' | 'BLOCKED';
  approved_pii_types: string[];
  blocked_pii_types: string[];
  pii_review_date: string | null;
  pii_scan_metadata?: {
    outcome?: 'complete' | 'partial' | 'skipped';
    reason?: string | null;
    reason_text?: string | null;
    scan_result?: {
      detectedPII?: Array<{
        type: string;
        pattern: string;
        matches: string[];
        count: number;
        risk_level: string;
        confidence: number;
        contexts: string[];
      }>;
    };
  };
}

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

function piiStillScanning(doc: { pii_status?: string; pii_scan_metadata?: { outcome?: string } }): boolean {
  if (doc.pii_status !== 'pending') return false;
  const outcome = doc.pii_scan_metadata?.outcome;
  return outcome !== 'partial' && outcome !== 'skipped';
}

const FOLLOWUP_TARGET_LABELS: Record<string, string> = {
  sop_builder: 'SOP Builder',
  process_maps: 'Process Maps',
  risk_mapping: 'Risk Mapping',
  data_cleaning: 'Data Cleaning',
  smart_data_analyst: 'Smart Data Analyst',
  intelligence: 'Jarvis Intelligence',
};

interface DocumentsProps {
  /** Extra controls rendered in the search/filter toolbar row, before "Add Process Metadata". */
  toolbarExtra?: React.ReactNode;
}

const Documents: React.FC<DocumentsProps> = ({ toolbarExtra }) => {
  const { currentProject } = useProject();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const isReturningToSmartStart = returnTo === 'smart-start';
  const shouldAutoOpenUpload = searchParams.get('openUpload') === 'true';
  const followupSessionIdRaw = searchParams.get('smartStartFollowupSessionId');
  const followupSessionId = followupSessionIdRaw ? parseInt(followupSessionIdRaw, 10) : null;
  const followupTarget = searchParams.get('followupTarget');
  const isSmartRouterFollowup = Number.isInteger(followupSessionId);
  const handleReturnToSmartStart = useCallback(() => {
    navigate('/app/smart-start');
  }, [navigate]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [securityFilter, setSecurityFilter] = useState('All Security');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!shouldAutoOpenUpload) return;
    autoOpenedRef.current = true;
    setIsUploadModalOpen(true);
  }, [shouldAutoOpenUpload]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isStreamingModalOpen, setIsStreamingModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [lastLoadTime, setLastLoadTime] = useState<number>(0);
  const [showSecurityDashboard, setShowSecurityDashboard] = useState(false);
  const [isPIIReviewModalOpen, setIsPIIReviewModalOpen] = useState(false);
  const [piiReviewDocument, setPIIReviewDocument] = useState<Document | null>(null);
  const [securityRefreshTrigger, setSecurityRefreshTrigger] = useState(0);
  const [documentAnalyses, setDocumentAnalyses] = useState<any[]>([]);
  const [isDocumentAnalysesLoading, setIsDocumentAnalysesLoading] = useState(false);
  const [isDocumentsExpanded, setIsDocumentsExpanded] = useState(true);

  const securityStats = useMemo(() => {
    const total = documents.length;
    const approved = documents.filter(d => d.pii_status === 'approved').length;
    const sanitized = documents.filter(d => d.pii_status === 'sanitized').length;
    const blocked = documents.filter(d => d.pii_status === 'blocked').length;
    const pending = documents.filter(d => d.pii_status === 'pending').length;
    const highRisk = documents.filter(d => ['high', 'critical'].includes(d.pii_risk_level)).length;

    return { total, approved, sanitized, blocked, pending, highRisk };
  }, [documents]);

  // 1. Optimized loadDocuments with better caching and error handling
const loadDocuments = useCallback(async (skipLoading = false, forceRefresh = false) => {
  if (!currentProject) return;

  // Move these outside the try block so they're accessible in catch
  let retryCount = 0;
  const maxRetries = 3;

  try {
    if (!skipLoading) setLoading(true);

    console.log(`📋 Loading documents, forceRefresh: ${forceRefresh}`);

    // AGGRESSIVE cache busting when force refreshing
    const params: any = {
      status: statusFilter !== 'All Status' ? statusFilter : undefined,
    };
    
    if (forceRefresh) {
      params._refresh = Date.now();
      params._cache_bust = Date.now();
      params._force = 'true';
    }

    const headers: any = {};
    if (forceRefresh) {
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      headers['Pragma'] = 'no-cache';
      headers['Expires'] = '0';
    }

    // ENHANCED: Add retry logic for network errors
    let response;

    while (retryCount < maxRetries) {
      try {
        response = await api.get(`/api/documents/project/${currentProject.id}`, {
          params,
          headers,
          timeout: 60000 // Increased timeout to 60 seconds
        });
        break; // Success, exit retry loop
      } catch (error: any) {
        retryCount++;
        console.warn(`📋 Documents load attempt ${retryCount} failed:`, error.message);
        
        // Check if it's a network error
        const isNetworkError = error.message?.includes('Network Error') || 
                              error.message?.includes('ERR_NETWORK') ||
                              error.message?.includes('Failed to fetch') ||
                              error.code === 'ERR_NETWORK';
        
        if (isNetworkError && retryCount < maxRetries) {
          console.log(`⏳ Retrying documents load in 2 seconds... (${retryCount}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        
        // If we've exhausted retries or it's not a network error, throw
        throw error;
      }
    }

    if (response && response.data && response.data.documents) {
      console.log(`📋 Documents loaded: ${response.data.documents.length} documents`);
      setDocuments(response.data.documents);
      setLastLoadTime(Date.now());

      // Load document analyses for RCSA Process Builder.
      //
      // Deliberately NOT awaited. This call feeds one panel, but on a project
      // with a large process map it is the slowest request on the screen and it
      // inherits the api client's five-minute default ceiling. Awaiting it here
      // held `loading` — and with it the "Loading documents..." placeholder and
      // the 0-document count — open for as long as it took, even though the
      // document list itself had already arrived and been stored above. It runs
      // on its own flag with its own bounded timeout instead, so the documents
      // panel resolves as soon as the documents do.
      setIsDocumentAnalysesLoading(true);
      api.get(`/api/analysis/results/${currentProject.id}`, { timeout: 30000 })
        .then((analysesResponse) => {
          if (analysesResponse.data && analysesResponse.data.document_analyses) {
            setDocumentAnalyses(analysesResponse.data.document_analyses);
          }
        })
        .catch((analysesError) => {
          console.warn('Failed to load document analyses:', analysesError);
          setDocumentAnalyses([]);
        })
        .finally(() => {
          setIsDocumentAnalysesLoading(false);
        });
    } else {
      console.warn('📋 Documents response is undefined or malformed:', response);
      setDocuments([]);
    }

    // Check if polling needed
    const needsPolling = response && response.data && response.data.documents
      ? response.data.documents.some(
          (doc: Document) =>
            doc.analysis_status === 'processing' || 
            doc.analysis_status === 'analyzing' ||
            piiStillScanning(doc)
        )
      : false;
    setShouldPoll(needsPolling);

  } catch (error: any) {
    console.error('Failed to load documents:', error);
    
    if (!skipLoading) {
      // Only show toast for final failure after all retries
      if (retryCount >= maxRetries) {
        if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
          toast.error('Network connection issues. Please check your connection and try again.', {
            duration: 5000
          });
        } else if (!error.message?.includes('timeout')) {
          // Don't show timeout errors as they're usually temporary
          toast.error('Failed to load documents');
        }
      }
    }
  } finally {
    if (!skipLoading) setLoading(false);
  }
}, [currentProject, statusFilter]);


  // 2. Lightweight status check for polling (doesn't reload full document list)
  const checkStatus = useCallback(async () => {
    if (!currentProject || !shouldPoll) return;

    try {
      const response = await api.get(`/api/documents/project/${currentProject.id}/status`, {
        timeout: 10000 // Increased from 3s to 10s
      });

      // Only reload full document list if status actually changed
      if (!response.data.needs_polling && shouldPoll) {
        // Processing completed, reload full list once
        await loadDocuments(true);
        setShouldPoll(false);
      } else if (response.data.needs_polling && !shouldPoll) {
        setShouldPoll(true);
      }

    } catch (error) {
      console.error('Status check failed:', error);
    }
  }, [currentProject, shouldPoll, loadDocuments]);

  // 3. Optimized polling with exponential backoff
  useEffect(() => {
    if (!currentProject) return;

    // Initial load
    loadDocuments();

    let pollInterval: NodeJS.Timeout;
    let pollCount = 0;

    if (shouldPoll) {
      const poll = () => {
        checkStatus();
        pollCount++;

        // Exponential backoff: start with 5s, max 30s
        const delay = Math.min(5000 + (pollCount * 2000), 30000);
        pollInterval = setTimeout(poll, delay);
      };

      // Start polling after 5 seconds to give server time to process
      pollInterval = setTimeout(poll, 5000);
    }

    return () => {
      if (pollInterval) clearTimeout(pollInterval);
    };
  }, [currentProject, shouldPoll, checkStatus]);

  useEffect(() => {
    if (!isUploadModalOpen && shouldPoll) {
      const timer = setTimeout(() => {
        loadDocuments(true);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isUploadModalOpen, shouldPoll, loadDocuments]);

  // 4. Optimized filtering with useMemo
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Apply search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.original_name.toLowerCase().includes(lowerSearchTerm) ||
        doc.category.toLowerCase().includes(lowerSearchTerm) ||
        (doc.description && doc.description.toLowerCase().includes(lowerSearchTerm))
      );
    }

    // Apply status filter
    if (statusFilter !== 'All Status') {
      filtered = filtered.filter(doc => doc.analysis_status === statusFilter);
    }

    // NEW: Apply security filter
    if (securityFilter !== 'All Security') {
      filtered = filtered.filter(doc => {
        switch (securityFilter) {
          case 'APPROVED':
            return doc.security_status === 'APPROVED';
          case 'SANITIZED':
            return doc.security_status === 'SANITIZED';
          case 'BLOCKED':
            return doc.security_status === 'BLOCKED';
          case 'High Risk':
            return ['high', 'critical'].includes(doc.pii_risk_level);
          case 'PII Detected':
            return doc.pii_types_detected.length > 0;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [documents, searchTerm, statusFilter, securityFilter]);

  // 5. Debounced search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const handleSecurityReview = useCallback(async (documentId: number, action: 'approve' | 'block' | 'sanitize') => {
    console.log(`🔥 Security review: document ${documentId}, action: ${action}`);
    
    try {
      // 1. IMMEDIATE UI UPDATE (optimistic)
      const expectedPiiStatus = action === 'approve' ? 'approved' : 
                              action === 'sanitize' ? 'sanitized' : 'blocked';
      const expectedSecurityStatus = action === 'approve' ? 'APPROVED' : 
                                    action === 'sanitize' ? 'SANITIZED' : 'BLOCKED';
      const expectedAnalysisStatus = action === 'block' ? 'failed' : 'completed';
      
      setDocuments(prevDocs => 
        prevDocs.map(doc => 
          doc.id === documentId 
            ? { 
                ...doc, 
                pii_status: expectedPiiStatus as 'pending' | 'approved' | 'sanitized' | 'blocked',
                security_status: expectedSecurityStatus as 'APPROVED' | 'SANITIZED' | 'BLOCKED',
                analysis_status: expectedAnalysisStatus as 'pending' | 'processing' | 'completed' | 'failed' | 'analyzing'
              }
            : doc
        )
      );

      // 2. BACKEND UPDATE
      const response = await api.post(`/api/documents/security/review/${documentId}`, {
        action,
        notes: `Manual review via Documents page`
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Security review failed');
      }

      toast.success(`Document ${action}d successfully`);
      
      // 3. SINGLE REFRESH AFTER SUCCESS - NO MULTIPLE CALLS
      console.log(`📄 Refreshing document list once after security review`);
      
      // Use setTimeout to avoid race conditions
      setTimeout(async () => {
        await loadDocuments(true, true);
        
        // Trigger security dashboard refresh ONCE with delay
        setTimeout(() => {
          setSecurityRefreshTrigger(prev => prev + 1);
        }, 1000);
      }, 500);
      
      console.log(`✅ Security review completed for document ${documentId}`);
      
    } catch (error: any) {
      console.error(`❌ Security review failed for document ${documentId}:`, error);
      
      // REVERT optimistic update on error
      await loadDocuments(true, true);
      
      if (error.response?.status === 401) {
        toast.error('Session expired - please login again');
      } else {
        toast.error(`Security review failed: ${error.message || 'Unknown error'}`);
      }
    }
  }, [loadDocuments]);


  const handleBulkSecurityReview = useCallback(async (action: 'approve' | 'block') => {
    const pendingDocs = filteredDocuments.filter(d => d.pii_status === 'pending');
    
    if (pendingDocs.length === 0) {
      toast.error('No documents requiring review');
      return;
    }

    try {
      const items = pendingDocs.map(d => ({ type: 'document', id: d.id }));
      
      await api.post('/api/security/bulk-review', {
        items,
        action,
        notes: `Bulk ${action} via Documents page`
      });

      toast.success(`Bulk ${action} completed for ${pendingDocs.length} documents`);
      
      // Refresh documents and trigger security dashboard refresh
      loadDocuments(true);
      
      // CHANGE: Add delay instead of immediate trigger
      setTimeout(() => {
        setSecurityRefreshTrigger(prev => {
          const newValue = prev + 1;
          console.log(`📊 Documents: Triggering security dashboard refresh after bulk operation (trigger: ${newValue})`);
          return newValue;
        });
      }, 8000); // CHANGED: from immediate to 8 second delay

    } catch (error) {
      console.error('Bulk security review failed:', error);
      toast.error('Bulk security review failed');
    }
  }, [filteredDocuments, loadDocuments]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleUploadSuccess = useCallback((uploadedDocs?: Array<{id: number, original_name: string, file_type: string}>) => {
    setShouldPoll(true);
    loadDocuments();

    setTimeout(() => {
      setSecurityRefreshTrigger(prev => prev + 1);
    }, 5000);

    if (isSmartRouterFollowup && followupSessionId) {
      (async () => {
        try {
          const result = await resumeSmartRouterAfterUpload(followupSessionId);
          if (result?.deep_link_url) {
            toast.success('Document uploaded — continuing your Smart Router workflow.');
            window.location.href = result.deep_link_url;
            return;
          }
          toast.success('Document uploaded — returning to Smart Router.');
          navigate(`/app/smart-start/${followupSessionId}`);
        } catch (err: any) {
          toast.error(`Could not resume Smart Router: ${err?.response?.data?.error || err.message || err}`);
        }
      })();
    }
  }, [loadDocuments, isSmartRouterFollowup, followupSessionId, navigate]);

  // 7. Optimized delete with optimistic updates
  const deleteDocument = useCallback(async (documentId: number) => {
    const originalDocuments = documents;
    try {
      setDocuments(docs => docs.filter(d => d.id !== documentId));

      await api.delete(`/api/documents/${documentId}`);
      toast.success('Document deleted successfully');
      
      // CHANGE: Add delay instead of immediate trigger
      setTimeout(() => {
        setSecurityRefreshTrigger(prev => {
          const newValue = prev + 1;
          console.log(`📊 Documents: Triggering security dashboard refresh after deletion (trigger: ${newValue})`);
          return newValue;
        });
      }, 3000); // CHANGED: from immediate to 3 second delay

    } catch (error) {
      // Revert on error
      setDocuments(originalDocuments);
      toast.error('Failed to delete document');
    }
  }, [documents]);

  // 8. Memoized utility functions
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'pending_selection': return 'text-purple-600 bg-purple-100';
      case 'processing': return 'text-blue-600 bg-blue-100';
      case 'analyzing': return 'text-purple-600 bg-purple-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  }, []);

  const formatFileSize = useCallback((bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // 9. Optimized view document handler
  const handleViewDocument = useCallback(async (document: Document) => {
    // Use document ID for download to avoid issues with duplicate names
    const token = localStorage.getItem('jarvis_access_token');

    try {
      // Use document ID instead of original name to ensure we get the correct document
      const response = await fetch(`${BACKEND_URL}/api/documents/download/${document.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (errorText.includes('Invalid or expired token')) {
          toast.error('Session expired. Please log in again.');
          localStorage.removeItem('jarvis_access_token');
          window.location.href = '/login';
          return;
        }
        
        // Try to parse error as JSON for detailed message
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            toast.error(errorData.message);
            return;
          }
        } catch {
          // Not JSON, use generic error
        }
        
        throw new Error('Failed to fetch file');
      }

      const contentType = response.headers.get('content-type') || '';
      const rawBlob = await response.blob();

      // For plain text files — render inline in new tab with correct MIME
      const isText = contentType.includes('text/plain') ||
        (document.file_type || '').includes('text/plain') ||
        (document.original_name || '').toLowerCase().endsWith('.txt');

      // Force correct MIME so browser renders inline instead of downloading
      const blob = contentType.includes('application/pdf')
        ? new Blob([rawBlob], { type: 'application/pdf' })
        : isText
          ? new Blob([rawBlob], { type: 'text/plain; charset=utf-8' })
          : rawBlob;

      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      // Give the new tab enough time to load the blob before revoking
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);

    } catch (err) {
      toast.error('Failed to view file. The file may have been moved or deleted.');
    }
  }, []);

  // 10. Memoized modal handlers
  const handleEditClick = useCallback((document: Document) => {
    setSelectedDocument(document);
    setIsEditModalOpen(true);
  }, []);

  const handleStreamingAnalysisClick = useCallback((document: Document) => {
    setSelectedDocument(document);
    setIsStreamingModalOpen(true);
  }, []);

  // 11. Show performance metrics in development
  const performanceInfo = useMemo(() => {
    if (process.env.NODE_ENV !== 'development') return null;

    return (
      <div className="text-xs text-gray-500 mb-2">
        Documents: {documents.length} |
        Filtered: {filteredDocuments.length} |
        Last load: {lastLoadTime ? new Date(lastLoadTime).toLocaleTimeString() : 'Never'} |
        Polling: {shouldPoll ? 'Active' : 'Inactive'}
      </div>
    );
  }, [documents.length, filteredDocuments.length, lastLoadTime, shouldPoll]);

  useEffect(() => {
    // Trigger security dashboard refresh when document processing completes
    const processingDocs = documents.filter(d => 
      d.analysis_status === 'processing' || 
      d.analysis_status === 'analyzing' ||
      piiStillScanning(d)
    );
    
    // If no documents are processing and we have documents, refresh security dashboard
    if (processingDocs.length === 0 && documents.length > 0) {
      const now = Date.now();
      const timeSinceLastRefresh = now - lastLoadTime;
      
      // CHANGE: Increase minimum time and add delay
      if (timeSinceLastRefresh > 10000) { // CHANGED: from 5000ms to 10000ms
        console.log(`📊 Documents: Auto-triggering security dashboard refresh (processing complete)`);
        
        // ADDED: Delay even for auto-refresh
        setTimeout(() => {
          setSecurityRefreshTrigger(prev => {
            const newValue = prev + 1;
            console.log(`📊 Documents: Auto-refresh security dashboard after processing complete (trigger: ${newValue})`);
            return newValue;
          });
        }, 2000); // ADDED: 2 second delay for auto-refresh
      }
    }
  }, [documents, lastLoadTime]);

  const followupTargetLabel = followupTarget
    ? (FOLLOWUP_TARGET_LABELS[followupTarget] || followupTarget.replace(/_/g, ' '))
    : 'your workflow';

  return (
    <div className="space-y-6">
      {isSmartRouterFollowup && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
          <div className="p-2 bg-white rounded-lg flex-shrink-0">
            <Upload className="w-5 h-5 text-amber-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-amber-900">
              Upload a document to start {followupTargetLabel}
            </div>
            <p className="text-xs text-amber-800 mt-0.5">
              Smart Router is waiting on a document. As soon as your upload finishes,
              you'll be taken straight into {followupTargetLabel} — no extra clicks needed.
            </p>
          </div>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 transition whitespace-nowrap flex-shrink-0"
          >
            <Upload className="w-4 h-4" />
            Upload now
          </button>
        </div>
      )}

      {isReturningToSmartStart && !isSmartRouterFollowup && (
        <div className="flex items-start justify-between gap-4 p-4 bg-lavender-bg border border-lavender-border rounded-xl">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 bg-white rounded-lg flex-shrink-0">
              <Compass className="w-5 h-5 text-lavender-deep" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-super-dark-purple">
                You came from Smart Router
              </div>
              <p className="text-xs text-gray-700 mt-0.5">
                Upload, categorize, and review documents into
                {currentProject ? ` "${currentProject.name}"` : ' the selected project'}.
                When you're done, click <strong>Done — return to Smart Router</strong>. The
                project's documents will be available there automatically.
              </p>
            </div>
          </div>
          <button
            onClick={handleReturnToSmartStart}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border bg-lavender border-lavender text-white hover:bg-lavender-deep transition-all whitespace-nowrap flex-shrink-0"
          >
            Done — return to Smart Router
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-super-dark-purple">Document Management</h1>
          <p className="text-xs text-gray-500">Upload, organize, and manage your RCSA documentation</p>
          {performanceInfo}
        </div>
      </div>

      {/* Security Stats - Compact Tags */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-white border border-gray-200 text-gray-600">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />{securityStats.total} Total
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-green-50 border border-green-200 text-green-700">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />{securityStats.approved} Approved
        </span>
        {securityStats.sanitized > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-yellow-50 border border-yellow-200 text-yellow-700">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />{securityStats.sanitized} Sanitized
          </span>
        )}
        {securityStats.blocked > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-red-50 border border-red-200 text-red-700">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />{securityStats.blocked} Blocked
          </span>
        )}
        {securityStats.pending > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-blue-50 border border-blue-200 text-blue-700">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />{securityStats.pending} Scanning
          </span>
        )}
        {securityStats.highRisk > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-orange-50 border border-orange-200 text-orange-700">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />{securityStats.highRisk} High Risk
          </span>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex space-x-3 items-center">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric-blue"
        >
          <option>All Status</option>
          <option>Pending</option>
          <option>Processing</option>
          <option>Completed</option>
          <option>Failed</option>
        </select>

        {toolbarExtra}

        <button
          onClick={() => {
            if (currentProject) {
              window.location.href = `/app/rcsa-process-builder`;
            }
          }}
          className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-1.5 text-xs font-medium"
        >
          <ClipboardList className="w-4 h-4" />
          <span>Add Process Metadata</span>
        </button>

        <button
          data-tour="upload-documents-button"
          onClick={() => setIsUploadModalOpen(true)}
          className="px-3 py-1.5 bg-electric-blue text-white rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-1.5 text-xs font-medium"
        >
          <Upload className="w-4 h-4" />
          <span>Upload Documents</span>
        </button>
      </div>

      {/* Uploaded Documents - Collapsible Container */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header with dropdown */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsDocumentsExpanded(!isDocumentsExpanded)}
        >
          <div className="flex items-center space-x-3">
            <Upload className="w-5 h-5 text-electric-blue" />
            <h3 className="text-lg font-semibold text-super-dark-purple">Uploaded Documents ({documents.length})</h3>
          </div>
          {isDocumentsExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </div>

        {/* Documents List - Collapsible */}
        {isDocumentsExpanded && (
          <div className="border-t border-gray-200">
            {loading && documents.length === 0 ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-electric-blue mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading documents...</p>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">
                  {documents.length === 0 ? 'No documents found' : 'No documents match your search'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredDocuments.map((document) => (
                  <DocumentRow
                    key={document.id}
                    document={document}
                    onEdit={handleEditClick}
                    onView={handleViewDocument}
                    onDelete={deleteDocument}
                    onStreamingAnalysis={handleStreamingAnalysisClick}
                    onSecurityReview={handleSecurityReview}
                    onPIIReview={(doc) => {
                      setPIIReviewDocument(doc);
                      setIsPIIReviewModalOpen(true);
                    }}
                    getStatusColor={getStatusColor}
                    formatFileSize={formatFileSize}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Process Metadata accessible via "Add Process Metadata" button in toolbar */}

      {/* Modals */}
      {currentProject && (
        <UploadDocumentsModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onSuccess={handleUploadSuccess}
          currentProject={currentProject}
          loadDocuments={loadDocuments}
        />
      )}
      <EditDocumentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={loadDocuments}
        document={selectedDocument}
      />
      {currentProject && (
        <StreamingAnalysisModal
          isOpen={isStreamingModalOpen}
          onClose={() => setIsStreamingModalOpen(false)}
          document={selectedDocument}
          projectId={currentProject.id}
          onAnalysisComplete={loadDocuments}
        />
      )}

      {isPIIReviewModalOpen && piiReviewDocument && (
        <PIIReviewModal
          document={piiReviewDocument}
          isOpen={isPIIReviewModalOpen}
          onClose={() => {
            setIsPIIReviewModalOpen(false);
            setPIIReviewDocument(null);
          }}
          onApprove={async (docId, approvedTypes) => {
            try {
              console.log('Approving PII types for document', docId, approvedTypes);

              // Get existing approved/blocked types from document
              const existingApproved = piiReviewDocument?.approved_pii_types || [];
              const existingBlocked = piiReviewDocument?.blocked_pii_types || [];

              // Combine with new approvals and remove duplicates
              const combinedApproved = [...existingApproved, ...approvedTypes];
              const allApprovedTypes = Array.from(new Set(combinedApproved));

              const response = await api.post(`/api/documents/pii/review/${docId}`, {
                approvedTypes: allApprovedTypes,
                blockedTypes: existingBlocked
              });

              if (response.data.success) {
                toast.success(`${approvedTypes.length} PII type(s) approved as false positives`);
                await loadDocuments(false, true);
                setIsPIIReviewModalOpen(false);
                setPIIReviewDocument(null);
              }
            } catch (error: any) {
              console.error('Error approving PII types:', error);
              toast.error(error.response?.data?.error || 'Failed to approve PII types');
            }
          }}
          onReject={async (docId, rejectedTypes) => {
            try {
              console.log('Rejecting PII types for document', docId, rejectedTypes);

              // Get existing approved/blocked types from document
              const existingApproved = piiReviewDocument?.approved_pii_types || [];
              const existingBlocked = piiReviewDocument?.blocked_pii_types || [];

              // Combine with new blocks and remove duplicates
              const combinedBlocked = [...existingBlocked, ...rejectedTypes];
              const allBlockedTypes = Array.from(new Set(combinedBlocked));

              const response = await api.post(`/api/documents/pii/review/${docId}`, {
                approvedTypes: existingApproved,
                blockedTypes: allBlockedTypes
              });

              if (response.data.success) {
                toast.success(`${rejectedTypes.length} PII type(s) blocked - Document blocked`);
                await loadDocuments(false, true);
                setIsPIIReviewModalOpen(false);
                setPIIReviewDocument(null);
              }
            } catch (error: any) {
              console.error('Error rejecting PII types:', error);
              toast.error(error.response?.data?.error || 'Failed to block PII types');
            }
          }}
        />
      )}
    </div>
  );
};

const PIIReviewModal: React.FC<{
  document: Document;
  isOpen: boolean;
  onClose: () => void;
  onApprove: (docId: number, approvedTypes: string[]) => void;
  onReject: (docId: number, rejectedTypes: string[]) => void;
}> = ({ document, isOpen, onClose, onApprove, onReject }) => {
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  // Helper to get status of a PII type
  const getTypeStatus = (type: string): 'approved' | 'blocked' | 'pending' => {
    if (document.approved_pii_types?.includes(type)) return 'approved';
    if (document.blocked_pii_types?.includes(type)) return 'blocked';
    return 'pending';
  };

  // Only show pending types that haven't been reviewed yet
  const pendingTypes = document.pii_types_detected.filter(type => getTypeStatus(type) === 'pending');
  const approvedTypes = document.pii_types_detected.filter(type => getTypeStatus(type) === 'approved');
  const blockedTypes = document.pii_types_detected.filter(type => getTypeStatus(type) === 'blocked');

  const toggleType = (type: string) => {
    // Don't allow selection of already reviewed types
    if (getTypeStatus(type) !== 'pending') return;

    const newSelected = new Set(selectedTypes);
    if (newSelected.has(type)) {
      newSelected.delete(type);
    } else {
      newSelected.add(type);
    }
    setSelectedTypes(newSelected);
  };

  const handleApproveSelected = async () => {
    const typesToApprove = Array.from(selectedTypes);
    if (typesToApprove.length === 0) {
      toast.error('Please select at least one PII type to approve');
      return;
    }
    setIsLoading(true);
    try {
      await onApprove(document.id, typesToApprove);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectSelected = async () => {
    const typesToReject = Array.from(selectedTypes);
    if (typesToReject.length === 0) {
      toast.error('Please select at least one PII type to reject');
      return;
    }
    setIsLoading(true);
    try {
      await onReject(document.id, typesToReject);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="bg-super-dark-purple text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-1">PII Detection Review</h2>
              <p className="text-gray-200 text-sm">{document.original_name}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Review Detected PII</p>
                <p>The system detected potential personally identifiable information. Please review each detection and approve (if false positive) or reject (if actual PII) items below.</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {/* Already Reviewed - Approved Types */}
            {approvedTypes.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-gray-900">Approved (False Positives) - {approvedTypes.length}</h3>
                </div>
                <div className="space-y-2">
                  {approvedTypes.map((type) => (
                    <div key={type} className="p-3 border-2 border-green-200 bg-green-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div>
                            <div className="font-medium text-gray-900">
                              {type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                            </div>
                            <div className="text-xs text-green-700 mt-0.5">Approved - Not actual PII</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Already Reviewed - Blocked Types */}
            {blockedTypes.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <XCircle className="w-5 h-5 text-red-600" />
                  <h3 className="font-semibold text-gray-900">Blocked (Actual PII) - {blockedTypes.length}</h3>
                </div>
                <div className="space-y-2">
                  {blockedTypes.map((type) => (
                    <div key={type} className="p-3 border-2 border-red-200 bg-red-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <XCircle className="w-5 h-5 text-red-600" />
                          <div>
                            <div className="font-medium text-gray-900">
                              {type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                            </div>
                            <div className="text-xs text-red-700 mt-0.5">Blocked - Contains actual PII</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Review */}
            {pendingTypes.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-gray-900">Pending Review - {pendingTypes.length}</h3>
                  <div className="text-sm text-gray-600">
                    {selectedTypes.size} selected
                  </div>
                </div>

                {pendingTypes.map((type) => {
                  const detectedPII =
                    document.pii_scan_metadata?.scan_result?.detectedPII ||
                    document.metadata?.pii_scan_result?.detectedPII || [];
                  const piiDetail = detectedPII.find((d: any) => d.type === type || d.pattern === type);
                  const examples = piiDetail?.matches || [];
                  const contexts = piiDetail?.contexts || [];
                  const count = piiDetail?.count || examples.length;
                  const confidence = piiDetail?.confidence;

                  return (
                    <div
                      key={type}
                      onClick={() => toggleType(type)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all mb-3 ${
                        selectedTypes.has(type)
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedTypes.has(type)}
                            onChange={() => toggleType(type)}
                            className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div>
                            <div className="font-medium text-gray-900">
                              {type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                            </div>
                            <div className="text-sm text-gray-500 mt-0.5">
                              {type.includes('DATE') && 'Date-like pattern detected'}
                              {type.includes('SSN') && 'Social Security Number pattern'}
                              {type.includes('PHONE') && 'Phone number pattern'}
                              {type.includes('EMAIL') && 'Email address pattern'}
                              {type.includes('ADDRESS') && 'Physical address pattern'}
                              {!type.includes('DATE') && !type.includes('SSN') && !type.includes('PHONE') &&
                               !type.includes('EMAIL') && !type.includes('ADDRESS') && 'Sensitive information pattern'}
                              {count > 0 && <span className="ml-2 text-orange-600 font-medium">{count} occurrence{count !== 1 ? 's' : ''}</span>}
                              {confidence !== undefined && <span className="ml-2 text-gray-400">{Math.round(confidence * 100)}% confidence</span>}
                            </div>
                          </div>
                        </div>
                        <Shield className={`w-5 h-5 flex-shrink-0 ${selectedTypes.has(type) ? 'text-purple-600' : 'text-gray-400'}`} />
                      </div>

                      {/* Examples from document */}
                      {examples.length > 0 && (
                        <div className="mt-3 ml-8" onClick={(e) => e.stopPropagation()}>
                          <div className="text-xs font-medium text-gray-600 mb-1">Detected examples:</div>
                          <div className="flex flex-wrap gap-1.5">
                            {examples.map((ex, i) => (
                              <span key={i} className="inline-block bg-red-50 border border-red-200 text-red-700 text-xs font-mono px-2 py-0.5 rounded">
                                {ex}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Context snippets */}
                      {contexts.length > 0 && examples.length === 0 && (
                        <div className="mt-3 ml-8" onClick={(e) => e.stopPropagation()}>
                          <div className="text-xs font-medium text-gray-600 mb-1">Context:</div>
                          <div className="space-y-1">
                            {contexts.slice(0, 2).map((ctx, i) => (
                              <div key={i} className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded font-mono truncate">
                                …{ctx}…
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {pendingTypes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p className="font-medium">All PII types have been reviewed</p>
                <p className="text-sm mt-1">Close this modal to continue</p>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex justify-between items-center gap-4">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <div className="flex gap-3">
              <button
                onClick={handleRejectSelected}
                className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={selectedTypes.size === 0 || isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Block Selected ({selectedTypes.size})
              </button>
              <button
                onClick={handleApproveSelected}
                className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={selectedTypes.size === 0 || isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Approve Selected ({selectedTypes.size})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 12. Separate DocumentRow component for better performance
const DocumentRow = React.memo(({
  document,
  onEdit,
  onView,
  onDelete,
  onStreamingAnalysis,
  onSecurityReview,
  onPIIReview,
  getStatusColor,
  formatFileSize
}: {
  document: Document;
  onEdit: (doc: Document) => void;
  onView: (doc: Document) => void;
  onDelete: (id: number) => void;
  onStreamingAnalysis: (doc: Document) => void;
  onSecurityReview: (id: number, action: 'approve' | 'block' | 'sanitize') => void;
  onPIIReview: (doc: Document) => void;
  getStatusColor: (status: string) => string;
  formatFileSize: (bytes: number) => string;
}) => {
  const SecurityBadge: React.FC<{ document: Document }> = ({ document }) => {
    // CRITICAL FIX: Use security_status as primary source, pii_status as fallback
    const actualSecurityStatus = document.security_status || (
      document.pii_status === 'blocked' ? 'BLOCKED' : 
      document.pii_status === 'sanitized' ? 'SANITIZED' : 
      document.pii_status === 'approved' ? 'APPROVED' : 'PENDING'
    );

    const getSecurityBadgeStyle = (securityStatus: string, piiStatus: string, riskLevel: string) => {
      if (securityStatus === 'BLOCKED' || piiStatus === 'blocked') {
        return 'bg-red-100 text-red-800 border border-red-200';
      }
      const outcome = document.pii_scan_metadata?.outcome;
      if (outcome === 'partial' || outcome === 'skipped') {
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      }
      if (securityStatus === 'SANITIZED' || piiStatus === 'sanitized') {
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      }
      if (securityStatus === 'APPROVED' || piiStatus === 'approved') {
        return 'bg-green-100 text-green-800 border border-green-200';
      }
      if (piiStatus === 'pending' && ['high', 'critical'].includes(riskLevel)) {
        return 'bg-orange-100 text-orange-800 border border-orange-200';
      }
      if (piiStatus === 'pending') {
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      }
      return 'bg-green-100 text-green-800 border border-green-200';
    };

    const getSecurityIcon = (securityStatus: string, piiStatus: string) => {
      if (securityStatus === 'BLOCKED' || piiStatus === 'blocked') {
        return <XCircle className="w-3 h-3" />;
      }
      const outcome = document.pii_scan_metadata?.outcome;
      if (outcome === 'partial' || outcome === 'skipped') {
        return <AlertTriangle className="w-3 h-3" />;
      }
      if (securityStatus === 'SANITIZED' || piiStatus === 'sanitized') {
        return <Eye className="w-3 h-3" />;
      }
      if (securityStatus === 'APPROVED' || piiStatus === 'approved') {
        return <CheckCircle className="w-3 h-3" />;
      }
      if (piiStatus === 'pending') {
        return <AlertTriangle className="w-3 h-3" />;
      }
      return <CheckCircle className="w-3 h-3" />;
    };

    const getSecurityText = (securityStatus: string, piiStatus: string) => {
      // A file that was actually blocked stays BLOCKED, including when the
      // scan that found the critical hit only covered part of the text.
      if (securityStatus === 'BLOCKED' || piiStatus === 'blocked') {
        return 'BLOCKED';
      }
      // An incomplete scan must not wear the SECURE / SANITIZED label just
      // because an earlier write stored APPROVED.
      const outcome = document.pii_scan_metadata?.outcome;
      if (outcome === 'partial') return 'PARTIAL';
      if (outcome === 'skipped') return 'SKIPPED';
      if (securityStatus === 'SANITIZED' || piiStatus === 'sanitized') {
        return 'SANITIZED';
      }
      if (securityStatus === 'APPROVED' || piiStatus === 'approved') {
        return 'SECURE';
      }
      if (piiStatus === 'pending') {
        return 'SCANNING';
      }
      return 'SECURE';
    };

    // Enhanced debugging info - click to see full status
    const handleDebugClick = () => {
      console.log(`🔍 Security Badge Debug for Document ${document.id}:`, {
        document_id: document.id,
        security_status: document.security_status,
        pii_status: document.pii_status,
        pii_risk_level: document.pii_risk_level,
        actual_security_status: actualSecurityStatus,
        pii_types_count: document.pii_types_detected?.length || 0,
        pii_types: document.pii_types_detected
      });
    };

    return (
      <div 
        className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer ${
          getSecurityBadgeStyle(actualSecurityStatus, document.pii_status, document.pii_risk_level)
        }`}
        title={`Security: ${actualSecurityStatus}, PII: ${document.pii_status?.toUpperCase()}, Risk: ${document.pii_risk_level?.toUpperCase()}, Types: ${document.pii_types_detected?.length || 0}${document.pii_scan_metadata?.reason_text ? `. ${document.pii_scan_metadata.reason_text}` : ''}`}
        onClick={handleDebugClick}
      >
        {getSecurityIcon(actualSecurityStatus, document.pii_status)}
        <span>{getSecurityText(actualSecurityStatus, document.pii_status)}</span>
        {document.pii_types_detected && document.pii_types_detected.length > 0 && (
          <span className="ml-1 bg-white bg-opacity-50 px-1 py-0.5 rounded text-xs font-bold">
            {document.pii_types_detected.length}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-light-purple rounded-lg flex items-center justify-center text-2xl">
            📄
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-900">{document.original_name}</h3>
            <p className="text-sm text-gray-500">{document.description || 'No description'}</p>
            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
              <span className="font-semibold">{document.category}</span>
              <span>•</span>
              <span>{formatFileSize(document.file_size)}</span>
              <span>•</span>
              <span>Uploaded {new Date(document.upload_date).toLocaleDateString()}</span>
            </div>

            {document.pii_types_detected.length > 0 && (
              <div className="mt-2">
                <button
                  onClick={() => onPIIReview(document)}
                  className="px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-md text-xs font-medium transition-colors flex items-center gap-1"
                >
                  <Shield className="w-3 h-3" />
                  {(() => {
                    const approvedCount = document.approved_pii_types?.length || 0;
                    const blockedCount = document.blocked_pii_types?.length || 0;
                    const pendingCount = document.pii_types_detected.length - approvedCount - blockedCount;

                    if (approvedCount === 0 && blockedCount === 0) {
                      return `Review ${document.pii_types_detected.length} PII Detection${document.pii_types_detected.length > 1 ? 's' : ''}`;
                    }

                    const parts: string[] = [];
                    if (pendingCount > 0) parts.push(`${pendingCount} Pending`);
                    if (approvedCount > 0) parts.push(`${approvedCount} Approved`);
                    if (blockedCount > 0) parts.push(`${blockedCount} Blocked`);

                    return `Review PII (${parts.join(', ')})`;
                  })()}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <span
            title={document.analysis_status === 'failed' ? document.failure_reason || 'No reason provided' : ''}
            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(document.analysis_status)}`}
          >
            {document.analysis_status}
          </span>

          <SecurityBadge document={document} />

          <div className="flex items-center space-x-1">
            {/* NEW: Security Review Actions for pending items */}
            {document.pii_status === 'pending' && (
              <div className="flex space-x-1 mr-2">
                <button
                  onClick={() => {
                    console.log(`🔄 Approving document ${document.id}`);
                    onSecurityReview(document.id, 'approve');
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
                  title="Approve document for use"
                >
                  ✓ Approve
                </button>
                <button
                  onClick={() => {
                    console.log(`🔄 Blocking document ${document.id}`);
                    onSecurityReview(document.id, 'block');
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
                  title="Block document due to security concerns"
                >
                  ✗ Block
                </button>
                {document.pii_types_detected && document.pii_types_detected.length > 0 && (
                  <button
                    onClick={() => {
                      console.log(`🔄 Sanitizing document ${document.id}`);
                      onSecurityReview(document.id, 'sanitize');
                    }}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
                    title="Sanitize PII and approve for use"
                  >
                    🔧 Sanitize
                  </button>
                )}
              </div>
            )}
            {/* {document.pii_status !== 'pending' && (
              <div className="mr-2">
                <span className="text-xs text-gray-500">
                  {document.pii_status === 'approved' && '✅ Reviewed'}
                  {document.pii_status === 'blocked' && '🚫 Blocked'}
                  {document.pii_status === 'sanitized' && '🔧 Sanitized'}
                </span>
              </div>
            )} */}
            <button
              title="Edit Metadata"
              className="p-2 text-gray-400 hover:text-electric-blue transition-colors"
              onClick={() => onEdit(document)}
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              title="View Document"
              className="p-2 text-gray-400 hover:text-electric-blue transition-colors"
              onClick={() => onView(document)}
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              title="Delete Document"
              onClick={() => onDelete(document.id)}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

DocumentRow.displayName = 'DocumentRow';

export default Documents;